import React from 'react';
import { Box, ButtonBase, Tooltip } from '@mui/material';
import { brand } from '../../theme';
import { angleLabel, angleName, angleSpokenName } from './multicamStudioModel';
import { playerTokens } from './multicamTheme';

interface AngleSwitcherProps {
  angles: Array<{ angleKey: string; label?: string | null }>;
  /** The angle on screen. */
  viewKey: string;
  /** The angle the creator's cut shows at the playhead. */
  liveKey: string | null;
  hasFootage: (angleKey: string) => boolean;
  onSelect: (angleKey: string) => void;
}

/** Keep keyboard focus where it was, so Space keeps controlling playback. */
const keepFocus = (event: React.MouseEvent) => event.preventDefault();

/**
 * Picks the angle on screen, inside the studio's dark transport bar. Each
 * button names its angle; a dot marks the angle the cut shows at the
 * playhead, and angles with no footage there are unavailable. Keys 1-9 do
 * the same.
 */
const AngleSwitcher: React.FC<AngleSwitcherProps> = ({ angles, viewKey, liveKey, hasFootage, onSelect }) => (
  <Box
    role="group"
    aria-label="Angle on screen"
    data-testid="angle-switcher"
    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mx: { xs: 0.25, sm: 0.75 } }}
  >
    {angles.map((angle, index) => {
      const selected = angle.angleKey === viewKey;
      const available = selected || hasFootage(angle.angleKey);
      const isLive = angle.angleKey === liveKey;
      const label = angleLabel(angle);
      const shortcut = index < 9 ? ` (${index + 1})` : '';
      const tooltip = [
        `${angleName(angle)}${shortcut}`,
        isLive ? 'Live in your cut here' : null,
        available ? null : 'No footage at this point',
      ].filter(Boolean).join(' · ');
      return (
        <Tooltip key={angle.angleKey} title={tooltip}>
          <span>
            <ButtonBase
              aria-pressed={selected}
              aria-label={`Show ${angleSpokenName(angle)}${isLive ? ', live in your cut' : ''}${available ? '' : ', no footage here'}`}
              data-angle={angle.angleKey}
              data-live={isLive ? 'true' : 'false'}
              disabled={!available}
              onMouseDown={keepFocus}
              onClick={() => onSelect(angle.angleKey)}
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                height: 30,
                minWidth: 32,
                px: 1,
                borderRadius: 2,
                border: `1px solid ${selected ? playerTokens.text : playerTokens.border}`,
                bgcolor: selected ? playerTokens.text : 'transparent',
                color: selected ? brand.ink : playerTokens.text,
                fontSize: 12.5,
                fontWeight: 700,
                lineHeight: 1,
                transition: 'background-color 120ms ease, border-color 120ms ease',
                '&:hover': { bgcolor: selected ? playerTokens.text : playerTokens.hover },
                '&:focus-visible': { outline: `2px solid ${playerTokens.focus}`, outlineOffset: 2 },
                '&.Mui-disabled': {
                  color: playerTokens.textDisabled,
                  borderColor: playerTokens.border,
                  borderStyle: 'dashed',
                },
              }}
            >
              <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{angle.angleKey}</Box>
              {label && (
                <Box
                  component="span"
                  sx={{
                    display: { xs: 'none', md: 'inline' },
                    maxWidth: 96,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                  }}
                >
                  {label}
                </Box>
              )}
              {isLive && (
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    position: 'absolute',
                    top: -3,
                    right: -3,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: playerTokens.live,
                    boxShadow: `0 0 0 2px ${brand.ink}`,
                  }}
                />
              )}
            </ButtonBase>
          </span>
        </Tooltip>
      );
    })}
  </Box>
);

export default AngleSwitcher;
